import React, { useEffect, useState, useRef } from "react";
import { useTheme } from "../../context/ThemeContext";
import { isStageOrDevEnv, getPayabliSrc, isAuthenticatedViaCookie, logout} from "../../utils/utils";
import { useLocation, useNavigate } from "react-router-dom";
import { expressCheckoutConfig, payabliConfigACH, payabliConfigCard } from "./Payabli";
import { useDispatch, useSelector } from "react-redux";
import { hideSideBar } from "../../store/sidebarSlice";
import { RootState } from "../../store";
import { updatePatientPostPayment, getPendingTransactions } from "../../utils/apis";
import "./LinkPayment.css";
import { FailurePopup } from '../shared/FailurePopup';
import VerificationModal from './VerificationModal';
import GNVerificationModal from './GNVerificationModal';
import UserInfoHeader from './UserInfoHeader';
import PaymentAccordion from './PaymentAccordion';
import SkeletonLoader from './SkeletonLoader';
import PaymentResultModal from './modals/PaymentResultModal';
import PaymentHeader from './PaymentHeader';
import BillSummaryModal from './BillSummaryModal';
import Sidebar from './Sidebar';
import MobileMenuButton from './MobileMenuButton';
import BillsSummary from './BillsSummary';

// Add type definitions at the top of the file, after imports
// PayabliComponent type definitions
interface PayabliComponentInstance {
  cleanup?: () => void;
  destroy?: () => void;
  updateConfig: (config: any) => void;
  payabliExec: (method: string, data: any) => void;
}

export const PAYMENT_TABS = {
  CARD: "card",
  BANK_DEBIT: "bank_debit",
  WALLET: 'wallet'
}

const SCHEDULE_FREQUENCIES = {
  WEEKLY: 'weekly',
  BIWEEKLY: 'biweekly',
  MONTHLY: 'monthly',
  QUARTERLY: 'quarterly'
}

const SPLIT_TYPES = {
  EQUAL: 'equal',
  MANUAL: 'manual'
}

// Function to map frequency to API format
const mapFrequencyToAPI = (frequency: string): string => {
  switch (frequency) {
    case SCHEDULE_FREQUENCIES.WEEKLY:
      return 'WEEKLY';
    case SCHEDULE_FREQUENCIES.BIWEEKLY:
      return 'BIWEEKLY';
    case SCHEDULE_FREQUENCIES.MONTHLY:
      return 'MONTHLY';
    default:
      return 'WEEKLY';
  }
};

const LinkPayment: React.FC = () => {
  
  const { theme } = useTheme();
  const [apiLoading, setApiLoading] = useState(false); 
  const [error, setError] = useState<string | null>(null);
  // Update the ref type
  const payabliComponentRef = useRef<PayabliComponentInstance | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isPaymentDone, setIsPaymentDone] = useState(false);
  const [selectedPaymentTab, setSelectedPaymentTab] = useState(PAYMENT_TABS.CARD);
  const [isPaying, setIsPaying] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false); 

  const [payabliToken, setPayabliToken] = useState<string | undefined>(undefined);
  const [pendingPaymentAmount, setPendingPaymentAmount] = useState<number>(0);
  const [payabliEntryId, setPayabliEntryId] = useState<string | undefined>(undefined);
  
  const validateUserAndFetchPayments = async  (guarantorLastName?: string, guarantorNumber?: string) => {
    try {
      const response = await getPendingTransactions(guarantorLastName, guarantorNumber);
      // Handle new patient accounts API response format
      if (response.status === 200 && response.data && response.data.accounts.length > 0) {
        
        const firstAccount = response?.data?.accounts[0];
        const userInfo = {
          name: `${firstAccount.guarantorNameFirst || ''} ${firstAccount.guarantorNameLast || ''}`.trim(),
          gn: firstAccount.guarantorNumber || guarantorNumber || 'N/A',
          email: firstAccount.guarantorEmail || undefined,
          phone: undefined, // Not available in new API
          guarantorLastName: firstAccount.guarantorNameLast || undefined
        };
  
        console.log('response?.data?.payabliToken', response?.data?.payabliToken);
        console.log('response?.data?.payabliEntityId', response?.data?.payabliEntityId);
        setPayabliToken(response?.data?.payabliToken);
        setPayabliEntryId(response?.data?.payabliEntityId);
        setPendingPaymentAmount(response?.data?.pendingPaymentAmount || 0);
        // Transform patient accounts to payment-like structure for UI compatibility
        const transformedPayments = response.data?.accounts.map((account: any) => ({
          totalDueAmount: account.accountPatientBalance ?? 0,
          id: account.accountId,
          amount: account.accountPatientBalance ?? 0,
          status: 'pending', // Default status for patient accounts
          dueDate: account.accountServiceDatetime,
          invoiceNumber: account.accountNumber,
          description: `Account ${account.accountNumber} - ${account.accountLocation || 'Medical Services'}`,
          payabliToken: undefined, // Not available in new API
          payabliEntryId: undefined, // Not available in new API
          currency: 'USD', // Default currency
          paymentType: 'ONE_TIME', // Default payment type
          createdAt: account.sourceFirstSeenAt,
          updatedAt: account.sourceLastSeenAt,
          statementDate: account.accountServiceDatetime,
          paymentDate: undefined,
          paymentIdentity: {
            firstName: account.guarantorNameFirst,
            lastName: account.guarantorNameLast,
            uniqueId: account.guarantorNumber,
            email: account.guarantorEmail,
            guarantorLastName: account.guarantorNameLast
          },
          // Additional patient account specific fields
          patientInfo: {
            patientNameFirst: account.patientNameFirst,
            patientNameLast: account.patientNameLast,
            patientNameMiddle: account.patientNameMiddle,
            patientBirthdate: account.patientBirthdate,
            patientRelationshipToGuarantor: account.patientRelationshipToGuarantor
          },
          accountDetails: {
            accountBalance: account.accountBalance,
            accountPatientBalance: account.accountPatientBalance,
            accountInsuranceBalance: account.accountInsuranceBalance,
            accountBilledChargeTotal: account.accountBilledChargeTotal,
            accountLocation: account.accountLocation
          }
        }));
        
        return {
          valid: true,
          user: userInfo,
          payments: transformedPayments,
          payabliToken: response?.data?.payabliToken,
          payabliEntryId: response?.data?.payabliEntityId
        };
      } 
  
      return {
        valid: true,
        user: 'N/A',
        payments: [],
        payabliToken: undefined,
        payabliEntryId: undefined
      };
    } catch (error) {
      console.error('Error validating user:', error);
      return { valid: false };
    }
  };


  // Add refs for the state variables
  const scheduledPaymentRef = useRef({
    isScheduled: false,
    frequency: SCHEDULE_FREQUENCIES.WEEKLY,
    splitType: SPLIT_TYPES.EQUAL,
    startDate: '',
    numberOfPayments: 1,
    amountPerPayment: '',
    manualPayments: [] as number[],
  });
  const [scheduledPayment, setScheduledPayment] = useState(scheduledPaymentRef.current);

  const pendingPaymentsRef = useRef<any[]>([]);
  const currentPaymentRef = useRef<any>(null);

  // Update ref values when state changes
  useEffect(() => {
    scheduledPaymentRef.current = scheduledPayment;
  }, [scheduledPayment]);
  
  const [modals, setModals] = useState({
    showVerify: false,
    showGN: false,
    showError: false,
    errorMsg: '',
    shouldRetryAfterError: false,
  });
  
  const [userInfo, setUserInfo] = useState<any>(null);
  const [expandedPaymentId, setExpandedPaymentId] = useState<string | null>(null);
  const [selectedPaymentForProcessing, setSelectedPaymentForProcessing] = useState<any>(null);
  const [isUserVerified, setIsUserVerified] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const isLoggedIn = localStorage.getItem("isAuthenticated");
    if(isLoggedIn) {
      setIsAuthenticated(() => {
        return true;
      });
    }
  }, []);
  
  const [pageReady, setPageReady] = useState(false); 
  
  const [resultModal, setResultModal] = useState({
    isOpen: false,
    isSuccess: false,
    message: '',
  });
  
  const dispatch = useDispatch()
  const isOpenSidbar = useSelector((state: RootState) => state.sidebar.isOpen);

  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const id = queryParams.get("id");
  const lastNameFromQuery = queryParams.get("lastName");
  const gnFromQuery = queryParams.get("gn");

  const [lastNameInput, setLastNameInput] = useState('');
  const [gnInput, setGnInput] = useState('');
  
  // Bill Summary Modal state
  const [billSummaryModal, setBillSummaryModal] = useState({
    isOpen: false,
    accountId: 0,
    accountNumber: '',
    guarantorName: '',
    guarantorNumber: '',
    totalAmount: 0
  });

  useEffect(() => {
    if (lastNameFromQuery) {
      setLastNameInput(lastNameFromQuery);
    }
    if (gnFromQuery) {
      setGnInput(gnFromQuery);
    }
  }, [lastNameFromQuery, gnFromQuery]);

  useEffect(() => {
    if (modals.shouldRetryAfterError && (lastNameFromQuery || lastNameInput)) {
      const timer = setTimeout(() => {
        if (id) {
          handleVerifyLastName();
        } else if (gnFromQuery || gnInput) {
          handleVerifyGN();
        }
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    setPageReady(true);
  }, []);

  useEffect(() => {
    if(isAuthenticated && !id) {
      const handleAuthenticated = async () => {
        setModals(prev => ({ ...prev, showVerify: false, showGN: false }));
        const res = await validateUserAndFetchPayments(undefined, undefined);
        if (res.valid) {
          setIsUserVerified(true);
          setUserInfo(res.user);
          pendingPaymentsRef.current = res.payments || [];
        } else {
          clearURLParams(); 
          setModals(prev => ({ ...prev, showVerify: false, showGN: true }));
        }
      }
      handleAuthenticated();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isUserVerified) {
      if (id) {
        if (lastNameFromQuery) {
          handleVerifyLastNameWithQueryParams();
        } else {
          setModals(prev => ({ ...prev, showVerify: true, showGN: false }));
        }
      } else if (!isAuthenticated) {
        if (lastNameFromQuery && gnFromQuery) {
          handleVerifyGNWithQueryParams();
        } else {
          setModals(prev => ({ ...prev, showGN: true, showVerify: false }));
        }
      }
    }
  }, [id, isUserVerified, lastNameFromQuery, gnFromQuery, isAuthenticated]);

  const getMinStartDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const calculateManualPayments = (totalAmount: number, amountPerPayment: number): number[] => {
    const numPayments = Math.floor(totalAmount / amountPerPayment);
    const remainder = totalAmount - (numPayments * amountPerPayment);
    
    const payments: number[] = Array(numPayments).fill(amountPerPayment);
    if (remainder > 0) {
      payments.push(remainder); // Add remainder as last payment
    }
    return [];
  };

  const isScheduledPaymentValid = () => {
    if (!scheduledPaymentRef.current.isScheduled) return true;
    
    if (!scheduledPaymentRef.current.startDate) return false;
    
    if (scheduledPaymentRef.current.splitType === SPLIT_TYPES.EQUAL) {
      return scheduledPaymentRef.current.numberOfPayments >= 1 && scheduledPaymentRef.current.numberOfPayments <= 52;
    } else {
      const amount = parseFloat(scheduledPaymentRef.current.amountPerPayment);
      return amount > 0 && amount <= (currentPaymentRef.current?.amount || 0);
    }
  };

  const updateURLWithParams = (params: { lastName?: string; gn?: string }) => {
    const newQueryParams = new URLSearchParams(location.search);
    
    if (params.lastName) {
      newQueryParams.set('lastName', params.lastName);
    }
    if (params.gn) {
      newQueryParams.set('gn', params.gn);
    }
    
    navigate(`${location.pathname}?${newQueryParams.toString()}`, { replace: true });
  };

  const clearURLParams = () => {
    const newQueryParams = new URLSearchParams(location.search);
    newQueryParams.delete('lastName');
    newQueryParams.delete('gn');
    
    navigate(`${location.pathname}?${newQueryParams.toString()}`, { replace: true });
  };

  const handleVerifyLastNameWithQueryParams = async () => {
    if (!lastNameFromQuery) return;
    
    setApiLoading(true);
    setError(null);
    try {
      const res = await validateUserAndFetchPayments(lastNameFromQuery, undefined);
      if (res.valid) {
        setIsUserVerified(true);
        setUserInfo(res.user);
        pendingPaymentsRef.current = res.payments || [];
      } else {
        clearURLParams(); 
        setModals(prev => ({ ...prev, errorMsg: 'Last name does not match our records. Please verify your information.' }));
        setModals(prev => ({ ...prev, showError: true }));
      }
    } catch (e) {
      clearURLParams(); 
      setModals(prev => ({ ...prev, errorMsg: 'An error occurred. Please try again.' }));
      setModals(prev => ({ ...prev, showError: true }));
    } finally {
      setApiLoading(false);
    }
  };

  const handleVerifyGNWithQueryParams = async () => {
    if (!lastNameFromQuery || !gnFromQuery) return;
    
    setApiLoading(true);
    setError(null);
    try {
      const res = await validateUserAndFetchPayments(lastNameFromQuery, gnFromQuery);
      if (res.valid) {
        setIsUserVerified(true);
        setUserInfo(res.user);
        pendingPaymentsRef.current = res.payments || [];
      } else {
          clearURLParams(); 
        setModals(prev => ({ ...prev, errorMsg: 'Guarantor Number or Last Name is incorrect. Please verify your information.' }));
        setModals(prev => ({ ...prev, showError: true }));
      }
    } catch (e) {
      clearURLParams(); 
      setModals(prev => ({ ...prev, errorMsg: 'An error occurred. Please try again.' }));
      setModals(prev => ({ ...prev, showError: true }));
    } finally {
      setApiLoading(false);
    }
  };

  useEffect(() => {
    if (scheduledPaymentRef.current.startDate && scheduledPaymentRef.current.numberOfPayments > 0) {
      setScheduledPayment(prev => ({ ...prev }));
    }
  }, [scheduledPaymentRef.current.startDate, scheduledPaymentRef.current.frequency, scheduledPaymentRef.current.numberOfPayments]);

  const refreshPaymentData = async () => {
    try {
      
      const lastNameToUse = lastNameFromQuery || undefined;
      const gnToUse = gnFromQuery || undefined;
      
      if (userInfo) {
        const res = await validateUserAndFetchPayments(
          lastNameToUse, 
          gnToUse
        );
        
        if (res.valid) {
          pendingPaymentsRef.current = res.payments || [];
        }
      } 
      // else if (id) {
      //   // TODO: As of now, it's not required
      //   const res = await validateUserAndFetchPayments(setResultModal, lastNameToUse, undefined, id);
      //   if (res.valid) {
      //     setUserInfo(res.user);
      //     pendingPaymentsRef.current = res.payments || [];
      //   }
      // }
      
      setModals(prev => ({ ...prev, showVerify: false }));
      setModals(prev => ({ ...prev, showGN: false }));
      setModals(prev => ({ ...prev, showError: false }));
    } catch (error) {
      console.error('Error refreshing payment data:', error);
    }
  };

  const handleViewBillSummary = (payment: any) => {
    setSelectedPaymentForProcessing(payment);
    setBillSummaryModal({
      isOpen: true,
      accountId: payment.id,
      accountNumber: payment.invoiceNumber || payment.id,
      guarantorName: userInfo?.name || 'N/A',
      guarantorNumber: userInfo?.gn || 'N/A',
      totalAmount: payment.totalDueAmount || payment.amount || 0
    });
  };

  const closeBillSummaryModal = () => {
    setBillSummaryModal(prev => ({ ...prev, isOpen: false }));
  };

  useEffect(() => {
    if (scheduledPaymentRef.current.splitType === SPLIT_TYPES.MANUAL && scheduledPaymentRef.current.amountPerPayment && currentPaymentRef.current?.amount) {
      const totalAmount = currentPaymentRef.current.amount;
      const perPayment = parseFloat(scheduledPaymentRef.current.amountPerPayment);
      const payments = calculateManualPayments(totalAmount, perPayment);
      setScheduledPayment(prev => ({ ...prev, manualPayments: payments }));
      setScheduledPayment(prev => ({ ...prev, numberOfPayments: payments.length }));
    }
  }, [scheduledPaymentRef.current.amountPerPayment, currentPaymentRef.current, scheduledPaymentRef.current.splitType]);

  useEffect(() => {
    if (scheduledPaymentRef.current.splitType === SPLIT_TYPES.EQUAL) {
      setScheduledPayment(prev => ({ ...prev, manualPayments: [] }));
      setScheduledPayment(prev => ({ ...prev, amountPerPayment: '' }));
    }
  }, [scheduledPaymentRef.current.splitType]);

  // Reset scheduled payment state when payments change
  useEffect(() => {
    if (pendingPaymentsRef.current.length > 0) {
      setScheduledPayment({
        isScheduled: false,
        frequency: SCHEDULE_FREQUENCIES.WEEKLY,
        splitType: SPLIT_TYPES.EQUAL,
        startDate: '',
        numberOfPayments: 1,
        amountPerPayment: '',
        manualPayments: [] as number[],
      });

      const pendingPayment = pendingPaymentsRef.current?.length > 0 ? pendingPaymentsRef.current[0].paymentIdentity : {};
      setBillSummaryModal({
        ...billSummaryModal,
        guarantorName: pendingPayment.guarantorLastName,
        guarantorNumber: pendingPayment.uniqueId,
      });
    }
  }, [pendingPaymentsRef.current]);

  // Add a ref to track if callback is already executed
  const callbackExecutedRef = useRef(false);
  
  // Create callback functions outside component
  const createCallbackFunctions = (isWallet: boolean = false) => {
    const functionCallBackSuccess = async (response: any) => {
      // Prevent multiple executions
      if (callbackExecutedRef.current) {
        return;
      }
      callbackExecutedRef.current = true;
      
      try {
        // Get the current scheduled payment state from ref
        const currentScheduledPayment = scheduledPaymentRef.current;
        const isScheduled = currentScheduledPayment.isScheduled;
        const referenceId =  isWallet ? response?.data?.responseData?.referenceId : response?.responseData?.referenceId || '';
        let amount = selectedPaymentForProcessing?.amount || currentPaymentRef.current?.amount || 0;
        
        let scheduledStartDate: string | undefined = undefined;
        let frequency: string | undefined = undefined;
        
        if (isScheduled) {
          scheduledStartDate = currentScheduledPayment.startDate || undefined;
          frequency = mapFrequencyToAPI(currentScheduledPayment.frequency) || undefined;
        }
        const pendingPayment = pendingPaymentsRef.current?.length > 0 ? pendingPaymentsRef.current[0].paymentIdentity : {};

        await updatePatientPostPayment(
          amount || currentPaymentRef.current?.amount,
          referenceId,
          isWallet ? response.data : response,
          isScheduled,
          scheduledStartDate,
          frequency,
          pendingPayment.guarantorLastName,
          pendingPayment.uniqueId
        );

        if(isScheduled) {
          // Set success modal and ensure it stays visible
          setResultModal({ isOpen: true, isSuccess: true, message: 'Your scheduled payment request was processed successfully!' });
        } else {
          // Set success modal and ensure it stays visible
          setResultModal({ isOpen: true, isSuccess: true, message: 'Your payment request was successful! Your payment will take 2-3 business days to process.' });
        }
        
        // Set states to indicate payment is complete
        setIsPaymentDone(true);
        setIsOpen(false);
        setIsPaying(false);
        
        await refreshPaymentData();
      } catch (error: any) {
        setResultModal({ isOpen: true, isSuccess: false, message: error?.response?.data?.message || 'There was an error processing your payment. Please try again.' });
        setIsPaymentDone(true);
        setIsOpen(false);
        setIsPaying(false);
      } finally {
        setApiLoading(false);
      }
    };

    const functionCallBackError = async (response: any) => {
      // Prevent multiple executions
      if (callbackExecutedRef.current) {
        return;
      }
      callbackExecutedRef.current = true;
      
      setResultModal({ isOpen: true, isSuccess: false, message: 'There was an error processing your request. Please try again.' });
      setIsPaymentDone(true);
      setIsOpen(false);
      setIsPaying(false);
      setApiLoading(false);
    };

    return { functionCallBackSuccess, functionCallBackError };
  };

  const closePopup = () => {
    setIsOpen(false);
    // Reset callback execution flag
    callbackExecutedRef.current = false;
    // Reset selected payment tab to default
    setSelectedPaymentTab(PAYMENT_TABS.CARD);
    // Cleanup the component and its callbacks
    if (payabliComponentRef.current) {
      try {
        // @ts-ignore
        if (payabliComponentRef.current.cleanup) {
          payabliComponentRef.current.cleanup();
        }
        // Ensure we remove all event listeners and references
        // @ts-ignore
        if (payabliComponentRef.current.destroy) {
          payabliComponentRef.current.destroy();
        }
      } catch (error) {
        console.error('Error cleaning up payabli component:', error);
      }
      payabliComponentRef.current = null;
    }
  };

  useEffect(() => {
    // Cleanup on component unmount
    return () => {
      closePopup();
    };
  }, []);

  useEffect(() => {
    if(isOpenSidbar) dispatch(hideSideBar())
  }, [isOpenSidbar, dispatch])

  useEffect(() => {
    if (!payabliComponentRef?.current) return;

    try {
      // Cleanup existing instance before updating config
      if (payabliComponentRef.current) {
        // @ts-ignore
        if (payabliComponentRef.current.cleanup) {
          payabliComponentRef.current.cleanup();
        }
      }

      if (selectedPaymentTab === PAYMENT_TABS.CARD) {
        // @ts-ignore
        payabliComponentRef.current.updateConfig(payabliConfigCard);
      } else if(selectedPaymentTab === PAYMENT_TABS.BANK_DEBIT) {
        // @ts-ignore
        payabliComponentRef.current.updateConfig(payabliConfigACH);
      } else {
        // Update expressCheckoutConfig with current payment amount before applying
        const { functionCallBackSuccess, functionCallBackError } = createCallbackFunctions(true);
        const paymentAmount = selectedPaymentForProcessing?.amount || currentPaymentRef.current?.amount || 0;
        (expressCheckoutConfig.expressCheckout as any).amount = paymentAmount;
        expressCheckoutConfig.functionCallBackSuccess = functionCallBackSuccess;
        expressCheckoutConfig.functionCallBackError = functionCallBackError;
        // @ts-ignore
        payabliComponentRef.current.updateConfig(expressCheckoutConfig);
      }
    } catch (error) {
      console.error('Error updating payabli config:', error);
      // If there's an error, reinitialize the component
      initializePayabliForm(currentPaymentRef.current);
    }
  }, [selectedPaymentTab]);

  // Function to handle payment from UserInfoHeader with paymentIdentityId
  const handleUserHeaderPayNow = async (amount: number, isScheduled: boolean, scheduledPaymentData?: any) => {
    try {
      setApiLoading(true);
      const firstPayment = pendingPaymentsRef.current[0];
      const finalPayment = {
        ...firstPayment,
        firstName: firstPayment.paymentIdentity?.firstName,
        lastName: firstPayment.paymentIdentity?.lastName,
        email: firstPayment.paymentIdentity?.email,
        phoneNumber: firstPayment.paymentIdentity?.phone,
        invoiceNumber: firstPayment.invoiceNumber || firstPayment.paymentIdentity?.uniqueId,
        amount: amount, // Use the amount from UserInfoHeader
        status: firstPayment.status,
        dueDate: firstPayment.dueDate,
        providerName: firstPayment.description || 'Payment for services',
        payabliToken: payabliToken || null,
        payabliEntryId: payabliEntryId || null
      }
      currentPaymentRef.current = finalPayment;

      console.log('scheduledPaymentData', isScheduled, scheduledPaymentData);
      
      // Update scheduled payment state if provided
      if (isScheduled) {
        const newScheduledPayment: {
          isScheduled: boolean;
          frequency: string;
          splitType: string;
          startDate: string;
          numberOfPayments: number;
          amountPerPayment: string;
          manualPayments: number[];
        } = {
          isScheduled: true,
          frequency: scheduledPaymentData.frequency,
          splitType: scheduledPaymentData.splitType,
          startDate: scheduledPaymentData.startDate,
          numberOfPayments: scheduledPaymentData.numberOfPayments,
          amountPerPayment: scheduledPaymentData.amountPerPayment,
          manualPayments: []
        };
        
        // Calculate manual payments if split type is manual
        if (scheduledPaymentData.splitType === SPLIT_TYPES.MANUAL && scheduledPaymentData.amountPerPayment && amount) {
          const payments = calculateManualPayments(amount, parseFloat(scheduledPaymentData.amountPerPayment));
          newScheduledPayment.manualPayments = payments;
          newScheduledPayment.numberOfPayments = payments.length;
        }

        console.log('newScheduledPayment', newScheduledPayment);
        
        setScheduledPayment(newScheduledPayment);
      }
      
      // Load Payabali script and open popup
      loadPayabliScript(finalPayment);
    } catch (error) {
      console.error('Error fetching payments by paymentIdentityId:', error);
      setError("Failed to load payment information");
    } finally {
      setApiLoading(false);
    }
  };

  const loadPayabliScript = (payment: any) => {
    const paymentToUse = payment || currentPaymentRef.current;
    
    if (!paymentToUse) {
      console.error('No payment data available');
      return;
    }

    // Clean up any existing instance before creating a new one
    if (payabliComponentRef.current) {
      // @ts-ignore
      if (payabliComponentRef.current.cleanup) {
        payabliComponentRef.current.cleanup();
      }
      // @ts-ignore
      if (payabliComponentRef.current.destroy) {
        payabliComponentRef.current.destroy();
      }
      payabliComponentRef.current = null;
    }
    
    if (!document.querySelector('script[src*="payabli.com/component.js"]')) {
      const script = document.createElement("script");
      script.src = getPayabliSrc();
      if(isStageOrDevEnv()) {
        script.dataset.test = "true";
      }
      script.onload = () => initializePayabliForm(paymentToUse);
      document.body.appendChild(script);
    } else {
      initializePayabliForm(paymentToUse);
    }
    setIsOpen(true);
  };

  const initializePayabliForm = (payment: any) => {
    // @ts-ignore
    if (typeof PayabliComponent === "undefined") {
      console.error("Payabli script not loaded.");
      return;
    }

    const currentPayment = payment;
    const paymentIdentityId = currentPayment?.paymentIdentity?.id;
    
    // Reset callback execution flag when initializing new form
    callbackExecutedRef.current = false;
    
    // Create callback functions
    const { functionCallBackSuccess, functionCallBackError } = createCallbackFunctions();

    // Configure the Payabli configs with the callbacks
    const configurePayabliConfig = (config: any) => {
      config.token = currentPayment?.payabliToken;
      config.entryPoint = currentPayment?.payabliEntryId;
      config.functionCallBackSuccess = functionCallBackSuccess;
      config.functionCallBackError = functionCallBackError;
      config.customerData = {
        firstName: currentPayment?.firstName,
        lastName: currentPayment?.lastName,
        customerNumber: currentPayment?.invoiceNumber,
        billingEmail: currentPayment?.email,
        additionalData: {
          paymentIdentityId: paymentIdentityId,
          guarantorLastName: billSummaryModal.guarantorName,
          guarantorNumber: billSummaryModal.guarantorNumber,
          type: 'PatientPayment'
        },
      };
    };

    // Configure all configs
    configurePayabliConfig(payabliConfigCard);
    configurePayabliConfig(payabliConfigACH);
    configurePayabliConfig(expressCheckoutConfig);
    
    // Configure expressCheckoutConfig with payment amount
    if (expressCheckoutConfig.expressCheckout) {
      const paymentAmount = currentPayment?.amount || 0;
      (expressCheckoutConfig.expressCheckout as any).amount = paymentAmount;
    }

    try {
      // Cleanup existing instance before creating new one
      if (payabliComponentRef.current) {
        // @ts-ignore
        if (payabliComponentRef.current.cleanup) {
          payabliComponentRef.current.cleanup();
        }
        // @ts-ignore
        if (payabliComponentRef.current.destroy) {
          payabliComponentRef.current.destroy();
        }
        payabliComponentRef.current = null;
      }

      // Create new instance with the appropriate config based on selected tab
      let initialConfig;
      switch (selectedPaymentTab) {
        case PAYMENT_TABS.BANK_DEBIT:
          initialConfig = payabliConfigACH;
          break;
        case PAYMENT_TABS.WALLET:
          initialConfig = expressCheckoutConfig;
          break;
        default:
          initialConfig = payabliConfigCard;
      }

      // Create new instance
      // @ts-ignore
      payabliComponentRef.current = new PayabliComponent(initialConfig);
    } catch (error) {
      console.error('Error initializing payabli component:', error);
      setResultModal({ 
        isOpen: true, 
        isSuccess: false, 
        message: 'There was an error initializing the payment form. Please try again.' 
      });
    }
  };

  const handlePayClick = (e: any) => {
    e.stopPropagation();
    if (isPaying) return;
    
    setIsPaying(true);
    setIsOpen(false);

    if (payabliComponentRef.current) {
      if (scheduledPaymentRef.current.isScheduled) {
        if (!scheduledPaymentRef.current.startDate) {
          setIsPaying(false);
          return;
        }

        const today = new Date();
        const selectedStartDate = new Date(scheduledPaymentRef.current.startDate);
        if (selectedStartDate < today) {
          setIsPaying(false);
          return;
        }

        const amountToUse = selectedPaymentForProcessing?.amount || currentPaymentRef.current?.amount || 0;
        
        if (scheduledPaymentRef.current.splitType === SPLIT_TYPES.EQUAL) {
          if (scheduledPaymentRef.current.numberOfPayments < 1 || scheduledPaymentRef.current.numberOfPayments > 52) {
            setIsPaying(false);
            return;
          }
        } else if (scheduledPaymentRef.current.splitType === SPLIT_TYPES.MANUAL) {
          const amount = parseFloat(scheduledPaymentRef.current.amountPerPayment);
          if (!scheduledPaymentRef.current.amountPerPayment || amount <= 0 || amount > amountToUse) {
            setIsPaying(false);
            return;
          }
        }

        // Validate amount before proceeding
        if (!amountToUse || amountToUse <= 0) {
          console.error('Invalid amount for scheduled payment:', amountToUse);
          setResultModal({ isOpen: true, isSuccess: false, message: 'Invalid payment amount. Please try again.' });
          setIsPaying(false);
          return;
        }

        const mappedFrequency = mapFrequencyToAPI(scheduledPaymentRef.current.frequency);

        // @ts-ignore
        payabliComponentRef.current.payabliExec("method", {
          paymentDetails: {
            totalAmount: amountToUse,
            serviceFee: 0,
            categories: [{ label: "payment", amount: amountToUse, qty: 1 }],
          },
          scheduleDetails: {
            frequency: mappedFrequency,
            startDate: scheduledPaymentRef.current.startDate,
            numberOfPayments: scheduledPaymentRef.current.splitType === SPLIT_TYPES.EQUAL ? 
              scheduledPaymentRef.current.numberOfPayments : 
              scheduledPaymentRef.current.manualPayments.length,
            amountPerPayment: scheduledPaymentRef.current.splitType === SPLIT_TYPES.EQUAL ? 
              (amountToUse / scheduledPaymentRef.current.numberOfPayments) : 
              parseFloat(scheduledPaymentRef.current.amountPerPayment),
            splitType: scheduledPaymentRef.current.splitType,
            manualPayments: scheduledPaymentRef.current.splitType === SPLIT_TYPES.MANUAL ? 
              scheduledPaymentRef.current.manualPayments : 
              undefined
          }
        });
      } else {
        const paymentAmount = selectedPaymentForProcessing?.amount || currentPaymentRef.current?.amount;
        
        // Validate amount before proceeding
        if (!paymentAmount || paymentAmount <= 0) {
          console.error('Invalid payment amount:', paymentAmount);
          setResultModal({ isOpen: true, isSuccess: false, message: 'Invalid payment amount. Please try again.' });
          setIsPaying(false);
          return;
        }

        const paymentMethod = selectedPaymentTab === PAYMENT_TABS.WALLET ? "wallet" : "pay";
        
        // Update expressCheckoutConfig if wallet payment is selected
        if (selectedPaymentTab === PAYMENT_TABS.WALLET) {
          // Create a copy of the original config to update
          const updatedExpressConfig = { ...expressCheckoutConfig };
          
          // Update the expressCheckout amount with the current payment amount
          if (updatedExpressConfig.expressCheckout) {
            (updatedExpressConfig.expressCheckout as any).amount = paymentAmount;
          }
          
          // Update the config before executing payment
          payabliComponentRef.current.updateConfig(updatedExpressConfig);
        }
        
        // @ts-ignore
        payabliComponentRef.current.payabliExec(paymentMethod, {
          paymentDetails: {
            totalAmount: paymentAmount,
            serviceFee: 0,
            categories: [{ label: "payment", amount: paymentAmount, qty: 1 }],
          }
        });
      }
    } else {
      setIsPaying(false);
    }
  };

  const handleVerifyLastName = async () => {
    setApiLoading(true);
    setError(null);
    
    const lastNameToUse = lastNameFromQuery || lastNameInput;
    
    try {
      const res = await validateUserAndFetchPayments(lastNameToUse || undefined, undefined);
      if (res.valid) {
        setModals(prev => ({ ...prev, showVerify: false }));
        setIsUserVerified(true);
        setModals(prev => ({ ...prev, shouldRetryAfterError: false }));
        
        updateURLWithParams({ lastName: lastNameToUse });
        
        setUserInfo(res.user);
        pendingPaymentsRef.current = res.payments || [];
      } else {
        clearURLParams();
        setModals(prev => ({ ...prev, errorMsg: 'Last name does not match our records. Please verify your information.' }));
        setModals(prev => ({ ...prev, showError: true }));
      }
    } catch (e) {
      clearURLParams();
      setModals(prev => ({ ...prev, errorMsg: 'An error occurred. Please try again.' }));
      setModals(prev => ({ ...prev, showError: true }));
    } finally {
      setApiLoading(false);
    }
  };

  const handleVerifyGN = async () => {
    setApiLoading(true);
    setError(null);
    
    const lastNameToUse = lastNameFromQuery || lastNameInput;
    const gnToUse = gnFromQuery || gnInput;
    
    try {
      const res = await validateUserAndFetchPayments(lastNameToUse || undefined, gnToUse || undefined);
      if (res.valid) {
        setModals(prev => ({ ...prev, showGN: false }));
        setIsUserVerified(true);
        setModals(prev => ({ ...prev, shouldRetryAfterError: false }));
        updateURLWithParams({ lastName: lastNameToUse, gn: gnToUse });
        
        setUserInfo(res.user);
        pendingPaymentsRef.current = res.payments || [];
      } else {
        clearURLParams();
        setModals(prev => ({ ...prev, errorMsg: 'Guarantor Number or Last Name is incorrect. Please verify your information.' }));
        setModals(prev => ({ ...prev, showError: true }));
      }
    } catch (e) { 
      clearURLParams();
      setModals(prev => ({ ...prev, errorMsg: 'An error occurred. Please try again.' }));
      setModals(prev => ({ ...prev, showError: true }));
    } finally {
      setApiLoading(false);
    }
  };

  const handlePaymentSelection = (payment: any) => {
    setSelectedPaymentForProcessing(payment);
    setExpandedPaymentId(payment.id);
  };

  const handleHideDetails = (e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedPaymentId(null);
    setSelectedPaymentForProcessing(null);
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  return (
    <div className={`payment-page  flex flex-row lg:flex-col ${pageReady ? 'payment-page-ready' : 'payment-page-loading'}`}>
      {/* Sidebar */}
      <Sidebar 
        userInfo={userInfo} 
        isOpen={isSidebarOpen} 
        onClose={closeSidebar} 
      />
      
      <VerificationModal
        isOpen={modals.showVerify}
        onClose={() => {
          setModals(prev => ({ ...prev, showVerify: false }));
          clearURLParams();
        }}
        onSubmit={handleVerifyLastName}
        lastNameInput={lastNameInput}
        setLastNameInput={setLastNameInput}
        loading={apiLoading}
      />

      <GNVerificationModal
        isOpen={modals.showGN}
        onClose={() => {
          setModals(prev => ({ ...prev, showGN: false }));
          clearURLParams();
        }}
        onSubmit={handleVerifyGN}
        gnInput={gnInput}
        setGnInput={setGnInput}
        lastNameInput={lastNameInput}
        setLastNameInput={setLastNameInput}
        loading={apiLoading}
      />

      <FailurePopup
        message={modals.errorMsg}
        isOpen={modals.showError}
        onClose={() => {
          setModals(prev => ({ ...prev, showError: false }));
          setModals(prev => ({ ...prev, shouldRetryAfterError: true }));
          if (id) {
            setModals(prev => ({ ...prev, showVerify: true }));
          } else {
            setModals(prev => ({ ...prev, showGN: true }));
          }
        }}
      />
      
      <PaymentResultModal
        isOpen={resultModal.isOpen || apiLoading || isPaying}
        isSuccess={resultModal.isSuccess}
        isLoading={!resultModal.isOpen && (apiLoading || isPaying)}
        message={resultModal.message}
        onClose={() => {
          setResultModal({ isOpen: false, isSuccess: false, message: '' });
          if(id){
            window.location.reload();
          }
        }}
      />

      {!isUserVerified && !modals.showError && !userInfo && (
        <div className="">
          {/* Mobile Header for St. Luke theme */}
          <div className="lg:hidden bg-white border-b border-gray-200 shadow-sm sticky top-0 z-30">
            <div className="px-4 py-3 flex items-center justify-between">
              <div className="flex items-center">
                <h1 className="text-lg font-bold text-gray-900">St. Luke Hospital</h1>
              </div>
              <MobileMenuButton isOpen={isSidebarOpen} onToggle={toggleSidebar} />
            </div>
          </div>
          
          <div className="lg:ml-80">
            <div className="w-full px-4 py-4">
              <div className="w-full payment-container">
                <SkeletonLoader />
              </div>
            </div>
          </div>
        </div>
      )}

      {userInfo && !modals.showVerify && !modals.showGN && (
        <div className="grow px-4 ">
          {/* Mobile Header for St. Luke theme */}
          <div className="lg:hidden ml-[-16px] mr-[-16px] bg-white border-b border-gray-200 shadow-sm sticky top-0 z-30">
            <div className="px-4 py-3 flex items-center justify-between flex-row-reverse">
              <div className="flex items-center">
                <h1 className="text-lg font-bold text-gray-900">St. Luke Hospital</h1>
              </div>
              <MobileMenuButton isOpen={isSidebarOpen} onToggle={toggleSidebar} />
            </div>
          </div>
          
          <div className="w-full">
            <div className="w-full grow py-4">
              <div className="w-full payment-container" data-testid="main-content">
              {pendingPaymentsRef.current.length > 0 ? (
                <>
                  <BillsSummary 
                    totalBills={pendingPaymentsRef.current.length}
                    totalAmountDue={pendingPaymentsRef.current.reduce((sum, payment) => sum + (payment.totalDueAmount || payment.amount || 0), 0)}
                  />
                  
                  <UserInfoHeader
                    key={`user-header-${pendingPaymentsRef.current.length}-${JSON.stringify(pendingPaymentsRef.current.map(p => p.id))}`}
                    userInfo={userInfo}
                    pendingPaymentsCount={pendingPaymentsRef.current.length}
                    payments={pendingPaymentsRef.current}
                    onLoadPayabliScript={handleUserHeaderPayNow}
                    isNewFlow={true}
                    scheduleFrequency={scheduledPayment.frequency}
                    setScheduleFrequency={(freq) => setScheduledPayment(prev => ({ ...prev, frequency: freq }))}
                    splitType={scheduledPayment.splitType}
                    setSplitType={(type) => setScheduledPayment(prev => ({ ...prev, splitType: type }))}
                    startDate={scheduledPayment.startDate}
                    setStartDate={(date) => setScheduledPayment(prev => ({ ...prev, startDate: date }))}
                    numberOfPayments={scheduledPayment.numberOfPayments}
                    setNumberOfPayments={(num) => setScheduledPayment(prev => ({ ...prev, numberOfPayments: num }))}
                    amountPerPayment={scheduledPayment.amountPerPayment}
                    setAmountPerPayment={(amount) => setScheduledPayment(prev => ({ ...prev, amountPerPayment: amount }))}
                    isScheduledPaymentValid={isScheduledPaymentValid}
                    getMinStartDate={getMinStartDate}
                    SCHEDULE_FREQUENCIES={SCHEDULE_FREQUENCIES}
                    SPLIT_TYPES={SPLIT_TYPES}
                    pendingPaymentAmount={pendingPaymentAmount}
                  />

                  <PaymentAccordion
                    payments={pendingPaymentsRef.current}
                    userInfo={userInfo}
                    expandedPaymentId={expandedPaymentId}
                    onPaymentSelect={handlePaymentSelection}
                    onHideDetails={handleHideDetails}
                    onViewBillSummary={handleViewBillSummary}
                    isScheduledPayment={scheduledPayment.isScheduled}
                    setIsScheduledPayment={() => setScheduledPayment(prev => ({ ...prev, isScheduled: !prev.isScheduled }))}
                    scheduleFrequency={scheduledPayment.frequency}
                    setScheduleFrequency={(freq) => setScheduledPayment(prev => ({ ...prev, frequency: freq }))}
                    splitType={scheduledPayment.splitType}
                    setSplitType={(type) => setScheduledPayment(prev => ({ ...prev, splitType: type }))}
                    startDate={scheduledPayment.startDate}
                    setStartDate={(date) => setScheduledPayment(prev => ({ ...prev, startDate: date }))}
                    numberOfPayments={scheduledPayment.numberOfPayments}
                    setNumberOfPayments={(num) => setScheduledPayment(prev => ({ ...prev, numberOfPayments: num }))}
                    amountPerPayment={scheduledPayment.amountPerPayment}
                    setAmountPerPayment={(amount) => setScheduledPayment(prev => ({ ...prev, amountPerPayment: amount }))}
                    isScheduledPaymentValid={isScheduledPaymentValid}
                    loadPayabliScript={loadPayabliScript}
                    getMinStartDate={getMinStartDate}
                    SCHEDULE_FREQUENCIES={SCHEDULE_FREQUENCIES}
                    SPLIT_TYPES={SPLIT_TYPES}
                    showBillSummaryModal={billSummaryModal.isOpen}
                    billSummaryModalData={{
                      accountId: billSummaryModal.accountId,
                      accountNumber: billSummaryModal.accountNumber,
                      guarantorName: billSummaryModal.guarantorName,
                      guarantorNumber: billSummaryModal.guarantorNumber,
                      totalAmount: billSummaryModal.totalAmount
                    }}
                    onCloseBillSummaryModal={() => setBillSummaryModal(prev => ({ ...prev, isOpen: false }))}
                  />
                </>
              ) : (
                <div className="bg-white rounded-lg shadow-md p-8 text-center">
                  <div className="mb-6">
                    <svg 
                      className="mx-auto h-16 w-16 text-gray-400" 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor" 
                      aria-hidden="true"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth={1.5} 
                        d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" 
                      />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Pending Payments</h3>
                  <p className="text-gray-500 mb-6">There are no pending payments for your account at this time.</p>
                  <button
                    onClick={() => window.location.reload()}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Refresh Page
                  </button>
                </div>
              )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className={`fixed bg-black/50 inset-0 flex justify-center items-center z-50 ${isOpen ? '' : 'hidden'}`} data-testid="payment-modal-overlay">
        <div className="w-[95%] max-w-sm sm:max-w-md mx-auto border-2 rounded-lg p-3 sm:p-6 bg-white shadow-lg relative modal-content" data-testid="payment-modal">
          <button
            className="absolute top-2 right-2 text-gray-600 hover:text-gray-800 text-lg sm:text-xl p-2"
            onClick={closePopup}
            data-testid="close-payment-modal"
          >
            ✕
          </button>
          <h4 className="text-base sm:text-lg font-bold text-gray-700 mb-4 text-center pt-2" data-testid="payment-method-title">Payment Method</h4>

          <div className="tabs mb-4 flex border-b overflow-x-auto" data-testid="payment-tabs">
            <button
              className={`flex-1 py-2 px-2 text-center text-xs sm:text-base font-bold min-w-[100px] ${
                selectedPaymentTab === PAYMENT_TABS.CARD ? `border-b-2 ${theme["border"]} ${theme["home-subheading"]}` : "text-gray-500"
              }`}
              onClick={() => setSelectedPaymentTab(PAYMENT_TABS.CARD)}
              data-testid="payment-card-tab"
            >
              Payment Card
            </button>
            <button
              className={`flex-1 py-2 px-2 text-center text-xs sm:text-base font-bold min-w-[100px] ${
                selectedPaymentTab === PAYMENT_TABS.BANK_DEBIT ? `border-b-2 ${theme["border"]} ${theme["home-subheading"]}` : "text-gray-500"
              }`}
              onClick={() => setSelectedPaymentTab(PAYMENT_TABS.BANK_DEBIT)}
              data-testid="bank-debit-tab"
            >
              Bank Debit
            </button>
            <button
              className={`flex-1 py-2 px-2 text-center text-xs sm:text-base font-bold min-w-[100px] ${
                selectedPaymentTab === PAYMENT_TABS.WALLET ? `border-b-2 ${theme["border"]} ${theme["home-subheading"]}` : "text-gray-500"
              }`}
              onClick={() => setSelectedPaymentTab(PAYMENT_TABS.WALLET)}
              data-testid="wallet-tab"
            >
              Wallets
            </button>
          </div>

          <div className="min-h-[300px] sm:min-h-[400px]" data-testid="payment-form-container">
            <div id="pay-component-card" data-testid="payabli-component"></div>
          </div>
          {
            <button
              id="btn-pay"
              data-testid="pay-button"
              className={`w-full py-2.5 sm:py-3 bg-gradient-to-r ${theme["gradient-home-button-text"]} text-white font-bold text-sm sm:text-base uppercase rounded-md flex items-center justify-center mt-4 button-hover ${selectedPaymentTab === PAYMENT_TABS.WALLET ? 'hidden' : ''} ${
                !isScheduledPaymentValid() ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              disabled={!isScheduledPaymentValid()}
              onClick={handlePayClick}
            >
              <img
                src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAYAAADgdz34AAAAAXNSR0IArs4c6QAAAPxJREFUSEvtldGNwjAMhr9/AnQTAJNwbMAG9CZATAC3ARvABjACN8mVDW4DI6Mg9VqStEWVeMAvjSrb3x8ndsTApoHz0wpgZgtgBXwGQWfgW5J/k5YFmNkBWEayOGSbIiQBQfkxJFgDp7AugE1Yz1M7yQG8BDNgLWlXVWpmrtwhP5LupWtsJgewEPEh6a8GmAC//k9SNE8rQCyBmd0EDAbI3aAbPOXURmEO0gCYmR/YHvAad7ES+KrfqEcAdxx3yVzxLSVNq7GPAPeb04tRP/A3oNm5taZ7l+g1S+RTc9SrCeAi6d8EiI0Kf8W6dvMFKLKjoqfyaFj2TX4WeAX0MmsZ8nx06AAAAABJRU5ErkJggg=="
                alt="Save Payment Icon"
                className="mr-2 w-4 h-4 sm:w-5 sm:h-5"
              />
              {scheduledPayment.isScheduled ? 'Schedule Payment' : 'Pay'}
            </button>
          }
        </div>
      </div>
      
      {/* Bill Summary Modal */}
      <BillSummaryModal
        isOpen={billSummaryModal.isOpen}
        onClose={closeBillSummaryModal}
        accountId={billSummaryModal.accountId}
        accountNumber={billSummaryModal.accountNumber}
        guarantorName={billSummaryModal.guarantorName}
        guarantorNumber={billSummaryModal.guarantorNumber}
        totalAmount={billSummaryModal.totalAmount}
      />
    </div>
  );
};

export default LinkPayment;
